import { useCallback, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  CurrentInspectionTemplateDocument,
  type CurrentInspectionTemplateQuery,
  type CurrentInspectionTemplateQueryVariables,
} from 'graphql-query-contracts';
import type { InspectionTemplate } from 'inspection-types';
import { mapGraphQLTemplateToDomain } from 'domains-inspection-client-mappers';
import { InvalidInspectionTemplateParamsError } from './errors/InvalidInspectionTemplateParamsError';
import { CurrentInspectionTemplateError } from './errors/CurrentInspectionTemplateError';

export type FetchCurrentInspectionTemplateParams = {
  id: string;
  channelId: string;
  version?: number | null;
};

export type FetchCurrentInspectionTemplateOutcome = {
  template: InspectionTemplate;
  baselineTemplate: InspectionTemplate | undefined;
};

export type UseCurrentInspectionTemplateResult = {
  template: InspectionTemplate | undefined;
  baselineTemplate: InspectionTemplate | undefined;
  loading: boolean;
  error: Error | undefined;
  called: boolean;
  fetchCurrentInspectionTemplate: (
    params: FetchCurrentInspectionTemplateParams
  ) => Promise<FetchCurrentInspectionTemplateOutcome>;
};

function isEmptyString(value: string): boolean {
  return value.trim() === '';
}

function validateParams(params: FetchCurrentInspectionTemplateParams): void {
  if (isEmptyString(params.id) || isEmptyString(params.channelId)) {
    throw new InvalidInspectionTemplateParamsError();
  }
}

function wrapError(err: unknown): Error {
  if (
    err instanceof CurrentInspectionTemplateError ||
    err instanceof InvalidInspectionTemplateParamsError
  ) {
    return err;
  }

  const message =
    err instanceof Error
      ? err.message
      : 'Failed to fetch current inspection template';

  return new CurrentInspectionTemplateError(message);
}

/**
 * Hook to fetch an inspection template directly from event aggregates via the
 * `currentInspectionTemplate` query.
 *
 * The returned `fetchCurrentInspectionTemplate` either resolves with the mapped
 * domain template or throws an error.
 * Local `template` / `error` state are also kept in sync for components that prefer
 * to react to state rather than awaiting the call.
 */
export function useCurrentInspectionTemplate(): UseCurrentInspectionTemplateResult {
  const [fetchTemplate, { loading, called }] = useLazyQuery<
    CurrentInspectionTemplateQuery,
    CurrentInspectionTemplateQueryVariables
  >(CurrentInspectionTemplateDocument, { fetchPolicy: 'network-only' });

  const [template, setTemplate] = useState<InspectionTemplate | undefined>(
    undefined
  );
  const [baselineTemplate, setBaselineTemplate] = useState<
    InspectionTemplate | undefined
  >(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const latestRequestIdRef = useRef(0);

  const fetchCurrentInspectionTemplate = useCallback(
    (
      params: FetchCurrentInspectionTemplateParams
    ): Promise<FetchCurrentInspectionTemplateOutcome> => {
      const requestId = ++latestRequestIdRef.current;
      const isLatest = () => latestRequestIdRef.current === requestId;

      setError(undefined);

      const run = async (): Promise<FetchCurrentInspectionTemplateOutcome> => {
        try {
          validateParams(params);

          const result = await fetchTemplate({
            variables: {
              id: params.id,
              channelId: params.channelId,
              version: params.version ?? undefined,
            },
          });

          if (result.error) {
            throw result.error;
          }

          const wrapper = result.data?.currentInspectionTemplate;

          if (!wrapper?.template) {
            throw new CurrentInspectionTemplateError(
              'Current inspection template response was empty'
            );
          }

          const mappedTemplate = mapGraphQLTemplateToDomain(wrapper.template);
          const mappedBaseline = wrapper.baselineTemplate
            ? mapGraphQLTemplateToDomain(wrapper.baselineTemplate)
            : undefined;

          if (isLatest()) {
            setTemplate(mappedTemplate);
            setBaselineTemplate(mappedBaseline);
          }

          return {
            template: mappedTemplate,
            baselineTemplate: mappedBaseline,
          };
        } catch (err) {
          const wrapped = wrapError(err);

          if (isLatest()) {
            setError(wrapped);
          }

          throw wrapped;
        }
      };

      return run();
    },
    [fetchTemplate]
  );

  return {
    template,
    baselineTemplate,
    loading,
    error,
    called,
    fetchCurrentInspectionTemplate,
  };
}
